import { useCallback } from 'react';
import { diktatEinfuegen } from './diktat';
import { useSpracherkennung, type Spracherkennung } from './use-spracherkennung';
import type { SpracherkennungAdapter } from './web-speech';

/**
 * Diktat in ein Textfeld: Spracherkennung plus deutsche Nachbearbeitung
 * (Satzzeichen, Groß-/Kleinschreibung, Anschluss an den vorhandenen Text).
 *
 * Der Aufrufer bekommt den fertigen neuen Feldwert und muss über Erkennung
 * nichts wissen. Für freie Erkennung ohne Feldbezug (Sprachsteuerung) direkt
 * `useSpracherkennung` verwenden.
 */
export interface DiktatHookOptionen {
  /**
   * Der aktuelle Feldwert — bewusst als Funktion. Der Callback der Erkennung
   * läuft außerhalb des Render-Zyklus; ein übergebener Wert wäre dort veraltet
   * und würde beim zweiten Satz den ersten überschreiben.
   */
  wert: () => string;
  /** Neuer vollständiger Feldwert nach dem Einfügen des Diktierten. */
  onWert: (neu: string) => void;
  sprache?: string;
  /** Austauschbar für Tests und für einen späteren Server-Erkenner. */
  adapter?: SpracherkennungAdapter;
}

export function useDiktat(optionen: DiktatHookOptionen): Spracherkennung {
  const { wert, onWert, sprache, adapter } = optionen;

  const onEndergebnis = useCallback(
    (roh: string) => {
      const bestehend = wert();
      const neu = diktatEinfuegen(bestehend, roh);
      if (neu !== bestehend) onWert(neu);
    },
    [onWert, wert],
  );

  return useSpracherkennung({ onEndergebnis, sprache, adapter, dauerhaft: true });
}
